import { useState, useCallback } from "react";
import {
  Backdrop,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Fade,
  IconButton,
  Modal,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsNoneRounded from "@mui/icons-material/NotificationsNoneRounded";
import DoneAllRounded from "@mui/icons-material/DoneAllRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import MarkEmailReadOutlined from "@mui/icons-material/MarkEmailReadOutlined";
import EventBusyOutlined from "@mui/icons-material/EventBusyOutlined";
import TodayOutlined from "@mui/icons-material/TodayOutlined";
import PriorityHighRounded from "@mui/icons-material/PriorityHighRounded";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/authContext";
import { resolveIdentity } from "../access";
import notificationsService from "../api/notificationsService";
import { useNotificationCenter } from "../hooks/useNotificationCenter";
import { MODAL, modalScroll } from "../components/modalTheme";
import { formatDateOnly } from "../utils/formatters";

const APP_TIMEZONE = "America/Mexico_City";

const palette = {
  text: "#334155",
  textMuted: "#94a3b8",
  textActive: "#0f172a",
  border: "rgba(15, 23, 42, 0.06)",
  hover: "rgba(15, 23, 42, 0.04)",
  accent: "#6366f1",
  accentSoft: "rgba(99, 102, 241, 0.08)",
  danger: "#ef4444",
  dangerSoft: "rgba(239, 68, 68, 0.08)",
  primary: "#2563eb",
  primarySoft: "rgba(37, 99, 235, 0.08)",
};

const fmtDate = (d) =>
  d
    ? new Intl.DateTimeFormat("es-MX", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: APP_TIMEZONE,
      }).format(new Date(d))
    : "—";

const fmtDateLong = (d) =>
  d
    ? new Intl.DateTimeFormat("es-MX", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: APP_TIMEZONE,
      }).format(new Date(d))
    : "—";

function StatusChip({ status }) {
  const map = {
    SENT: { label: "Publicado", color: "#16a34a", bg: "rgba(34, 197, 94, 0.12)" },
    FAILED: { label: "Fallido", color: "#dc2626", bg: "rgba(239, 68, 68, 0.12)" },
  };
  const s = map[status] || map.SENT;
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{
        height: 20,
        fontSize: 11,
        fontWeight: 600,
        color: s.color,
        bgcolor: s.bg,
        border: "none",
      }}
    />
  );
}

function FollowUpRow({ lead, kind, onClick }) {
  const urgent = kind === "urgent";
  const overdue = kind === "overdue";
  const Icon = urgent ? PriorityHighRounded : overdue ? EventBusyOutlined : TodayOutlined;
  const color = urgent ? "#be123c" : overdue ? palette.danger : palette.primary;
  const soft = urgent ? "rgba(190, 18, 60, 0.1)" : overdue ? palette.dangerSoft : palette.primarySoft;
  const when = urgent
    ? "Urgente · atender de inmediato"
    : `${overdue ? "Vencido" : "Para hoy"} · ${formatDateOnly(lead.nextFollowUpAt)}`;

  return (
    <Box
      onClick={() => onClick(lead)}
      sx={{
        display: "flex",
        gap: 1.25,
        px: 2,
        py: 1.35,
        cursor: "pointer",
        borderBottom: `1px solid ${palette.border}`,
        bgcolor: soft,
        transition: "background-color 0.15s ease",
        "&:hover": { filter: "brightness(0.98)" },
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          flexShrink: 0,
          borderRadius: "50%",
          bgcolor: "#fff",
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: `1px solid ${color}33`,
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: 13.5,
            fontWeight: 700,
            color: palette.textActive,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {lead.companyName}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: palette.textMuted, mt: 0.25 }}>
          {when}
          {lead.contactName ? ` · ${lead.contactName}` : ""}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 8,
          height: 8,
          mt: 0.75,
          borderRadius: "50%",
          bgcolor: color,
          flexShrink: 0,
        }}
      />
    </Box>
  );
}

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    allowed,
    canSeeEmails,
    followUpRows,
    followUpCount,
    items,
    unreadEmails,
    emailUnreadInTray,
    badgeCount,
    loading,
    loadTray,
    markAllEmailsRead,
    markEmailRead,
  } = useNotificationCenter(user);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const open = Boolean(anchorEl);
  const empty = !loading && followUpRows.length === 0 && items.length === 0;
  const hiddenEmailUnread = Math.max(0, unreadEmails - emailUnreadInTray);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    loadTray();
  };

  const handleClose = () => setAnchorEl(null);

  const openFollowUp = (lead) => {
    handleClose();
    const identity = resolveIdentity(user);
    const base = (identity.grants.crm === "agent" || identity.grants.crm === "marketer") && identity.account !== "admin" ? "/crm/ejecutivo" : "/crm/admin";
    navigate(`${base}?lead=${lead._id}`);
  };

  const openDetail = async (item) => {
    try {
      handleClose();
      setSelected({ ...item, html: null });
      setDetailLoading(true);
      const res = await notificationsService.get(item._id);
      setSelected(res.data.data);
      if (!item.readAt) markEmailRead(item._id);
    } catch {
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => setSelected(null);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await markAllEmailsRead();
    } catch {
      /* silencioso */
    }
  }, [markAllEmailsRead]);

  if (!allowed) return null;

  return (
    <>
      <Tooltip title="Notificaciones" arrow>
        <IconButton
          onClick={handleOpen}
          aria-label="Notificaciones"
          sx={{
            color: open ? palette.accent : palette.text,
            borderRadius: "12px",
            bgcolor: open ? palette.accentSoft : "transparent",
            "&:hover": { backgroundColor: palette.hover },
          }}
        >
          <Badge
            badgeContent={badgeCount}
            max={99}
            overlap="circular"
            sx={{
              "& .MuiBadge-badge": {
                bgcolor: "#ef4444",
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                minWidth: 16,
                height: 16,
                px: 0.5,
                boxShadow: "0 0 0 2px #ffffff",
              },
            }}
          >
            <NotificationsNoneRounded sx={{ fontSize: 22 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: { xs: "calc(100vw - 24px)", sm: 380 },
              maxWidth: 380,
              borderRadius: "16px",
              border: `1px solid ${palette.border}`,
              boxShadow: "0 16px 40px rgba(15, 23, 42, 0.12)",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${palette.border}`,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: palette.textActive }}>
            Notificaciones
            {badgeCount > 0 && (
              <Typography component="span" sx={{ fontSize: "0.8125rem", color: palette.textMuted, ml: 1 }}>
                ({badgeCount})
              </Typography>
            )}
          </Typography>
          {canSeeEmails && unreadEmails > 0 && (
            <Button
              onClick={handleMarkAllRead}
              startIcon={<DoneAllRounded sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                fontWeight: 600,
                color: palette.accent,
                minWidth: 0,
                px: 1,
                "&:hover": { bgcolor: palette.accentSoft },
              }}
            >
              Marcar leídas
            </Button>
          )}
        </Box>

        <Box sx={{ maxHeight: 420, overflowY: "auto", ...modalScroll }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <CircularProgress size={22} />
            </Box>
          ) : empty ? (
            <Box sx={{ p: 4, textAlign: "center", color: palette.textMuted }}>
              <EmailOutlined sx={{ fontSize: 36, opacity: 0.4, mb: 1 }} />
              <Typography sx={{ fontSize: 13.5 }}>No tienes notificaciones.</Typography>
            </Box>
          ) : (
            <>
              {followUpRows.length > 0 && (
                <>
                  <Box sx={{ px: 2, pt: 1.25, pb: 0.5 }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: palette.textMuted,
                      }}
                    >
                      Seguimientos por atender ({followUpCount})
                    </Typography>
                  </Box>
                  {followUpRows.map(({ lead, kind }) => (
                    <FollowUpRow
                      key={`${kind}-${lead._id}`}
                      lead={lead}
                      kind={kind}
                      onClick={openFollowUp}
                    />
                  ))}
                </>
              )}

              {canSeeEmails && items.length > 0 && (
                <>
                  <Box sx={{ px: 2, pt: 1.25, pb: 0.5 }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: palette.textMuted,
                      }}
                    >
                      Resúmenes semanales
                      {emailUnreadInTray > 0 ? ` (${emailUnreadInTray} no leídos)` : ""}
                    </Typography>
                  </Box>
                  {items.map((it) => {
                    const isUnread = !it.readAt;
                    return (
                      <Box
                        key={it._id}
                        onClick={() => openDetail(it)}
                        sx={{
                          display: "flex",
                          gap: 1.25,
                          px: 2,
                          py: 1.5,
                          cursor: "pointer",
                          borderBottom: `1px solid ${palette.border}`,
                          bgcolor: isUnread ? palette.accentSoft : "transparent",
                          transition: "background-color 0.15s ease",
                          "&:hover": { bgcolor: palette.hover },
                          "&:last-of-type": { borderBottom: "none" },
                        }}
                      >
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            flexShrink: 0,
                            borderRadius: "50%",
                            bgcolor: palette.accentSoft,
                            color: palette.accent,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <EmailOutlined sx={{ fontSize: 20 }} />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: 13.5,
                              fontWeight: isUnread ? 700 : 500,
                              color: palette.textActive,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {it.subject}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                            <Typography sx={{ fontSize: 11.5, color: palette.textMuted }}>
                              {fmtDate(it.createdAt)}
                            </Typography>
                            <StatusChip status={it.status} />
                          </Box>
                        </Box>
                        {isUnread && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              mt: 0.75,
                              borderRadius: "50%",
                              bgcolor: palette.accent,
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                  {hiddenEmailUnread > 0 && (
                    <Typography sx={{ px: 2, py: 1.25, fontSize: 11.5, color: palette.textMuted }}>
                      +{hiddenEmailUnread} resumen{hiddenEmailUnread !== 1 ? "es" : ""} no leído
                      {hiddenEmailUnread !== 1 ? "s" : ""} más antiguo{hiddenEmailUnread !== 1 ? "s" : ""}
                    </Typography>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Popover>

      <Modal
        open={Boolean(selected)}
        onClose={closeDetail}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: { sx: { backgroundColor: MODAL.backdrop, backdropFilter: "blur(4px)" } },
        }}
      >
        <Fade in={Boolean(selected)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "94vw", sm: "min(720px, 94vw)" },
              maxHeight: "88vh",
              display: "flex",
              flexDirection: "column",
              bgcolor: MODAL.surface,
              borderRadius: MODAL.radius,
              boxShadow: MODAL.shadow,
              overflow: "hidden",
              outline: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
                p: 2.5,
                borderBottom: `1px solid ${MODAL.border}`,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: MODAL.text }}>
                    {selected?.subject}
                  </Typography>
                  {selected && <StatusChip status={selected.status} />}
                </Box>
                <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <Typography sx={{ fontSize: 12.5, color: MODAL.textSecondary }}>
                    <b>Para:</b> {(selected?.recipients || []).join(", ") || "—"}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: MODAL.textSecondary }}>
                    <b>Fecha:</b> {fmtDateLong(selected?.createdAt)}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: MODAL.textSecondary }}>
                    <b>Origen:</b>{" "}
                    {selected?.trigger === "MANUAL"
                      ? `Manual (${selected?.sentByName})`
                      : "Programado"}
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={closeDetail} size="small" aria-label="Cerrar">
                <CloseRounded fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ flex: 1, overflow: "auto", ...modalScroll }}>
              {detailLoading || !selected?.html ? (
                <Box
                  sx={{
                    p: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1.5,
                    color: MODAL.textMuted,
                  }}
                >
                  {detailLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <>
                      <MarkEmailReadOutlined sx={{ fontSize: 44, opacity: 0.3 }} />
                      <Typography sx={{ fontSize: 13.5 }}>Sin contenido para mostrar.</Typography>
                    </>
                  )}
                </Box>
              ) : (
                <iframe
                  title="Vista de la notificación"
                  srcDoc={selected.html}
                  style={{
                    width: "100%",
                    height: "60vh",
                    border: "none",
                    display: "block",
                    background: "#fff",
                  }}
                />
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
}
